/**
 * [공통] Router 네비게이션
 * @module @router
 */

import * as VueRouter from 'vue-router'
import store from '@/user/store'
import { checkUserInfo } from '@/user/helpers/security.helper'
import { homepageRouteHandler, routeToUrl, signInWithUrl } from '@/user/homepage-integration'

/**
 * @constant
 * @type {array<object.<name:string, path:string, meta:object, component:function>>}
 * @default 컴포넌트 매핑 및 컴포넌트 옵션 설정
 */
const routes = [
  {
    path: '/',
    component: () => import('@/user/layouts/MainLayout.vue'),
    children: [
      {
        name: 'Home',
        path: '',
        component: () => import('@/user/views/Home.vue'),
      },
      {
        name: 'AccountEduCenter',
        path: '/account',
        redirect: { name: 'Applied' },
        meta: {
          authRequired: true,
          dialogBgTransparent: true,
        },
        component: () => import('@/user/views/account/EduCenter.vue'),
        children: [
          {
            name: 'Applied',
            path: 'applied',
            component: () => import('@/user/components/molecules/account/LectureApplied.vue'),
            meta: {
              homepagePath: '/account/edu-center',
            },
          },
          {
            name: 'Completed',
            path: 'completed',
            component: () => import('@/user/components/molecules/account/LectureCompleted.vue'),
            meta: {
              homepagePath: false,
            },
          },
          {
            name: 'CertificationExamApply',
            path: 'certification-apply',
            component: () => import('@/user/components/molecules/account/CertificationApplied.vue'),
            meta: {
              homepagePath: false,
            },
          },
          {
            name: 'CertificationExamResult',
            path: 'certification-result',
            component: () =>
              import('@/user/components/molecules/account/CertificationCompleted.vue'),
            meta: {
              homepagePath: false,
            },
          },
          {
            name: 'Certification',
            path: 'Certification',
            component: () => import('@/user/components/molecules/account/Certificate.vue'),
            meta: {
              homepagePath: false,
            },
          },
        ],
      },
      {
        name: 'Attend',
        path: '',
        component: () => import('@/user/views/register/Main.vue'),
        redirect: { path: '/attend' },
        children: [
          {
            name: 'AttendList',
            path: 'attend',
            component: () => import('@/user/views/register/Lecture.vue'),
            meta: {
              homepagePath: '/resource/edu-center/attend',
            },
          },
          {
            name: 'CertificationInstruction',
            path: 'certification/essential',
            component: () => import('@/user/views/certification/Instruction.vue'),
            meta: {
              homepagePath: '/resource/edu-center/certification/essential',
            },
          },
          {
            name: 'CertificationExamDate',
            path: 'certification/date',
            component: () => import('@/user/views/certification/Exam.vue'),
            meta: {
              homepagePath: '/resource/edu-center/certification/date',
            },
          },
          {
            name: 'Verify',
            path: 'certification/search',
            component: () => import('@/user/views/certification/Verify.vue'),
            meta: {
              homepagePath: '/resource/edu-center/certification/search',
            },
          },
          {
            name: 'AttendDetail',
            path: 'attend/detail/:id',
            component: () => import('@/user/views/register/Detail.vue'),
            meta: {
              homepagePath: '/resource/edu-center/attend/detail/{{params.id}}',
            },
          },
          {
            name: 'AttendRegister',
            path: 'attend/register/:id',
            component: () => import('@/user/views/register/Register.vue'),
            meta: {
              authRequired: true,
              homepagePath: '/resource/edu-center/attend/register/{{params.id}}',
            },
          },
        ],
      },
      {
        name: 'terms-view',
        path: '/terms/:id',
        props: true,
        component: () => import('@/user/views/terms/TermsView.vue'),
        meta: {
          homepagePath: '/resource/edu-center/terms/{{params.id}}',
        },
      },
    ],
  },
  {
    name: 'InternalServerError',
    path: '/error',
    meta: {
      authorize: 'permitAll',
    },
    component: () => import('@/user/views/error/InternalServerError.vue'),
  },
  {
    name: 'NotFound',
    path: '/:catchAll(.*)',
    meta: {
      authorize: 'permitAll',
    },
    component: () => import('@/user/views/error/NotFoundView.vue'),
  },
]

/**
 * 라우트 인스턴스 생성
 * @alias module:@router
 * @method createRouter
 * @param {RouterHistory} RouterHistory Creates an HTML5 history
 * @param {RouteRecordRaw[]} RouteRecordRaw 라우트 목록 초기화
 * @param {RouterScrollBehavior} RouterScrollBehavior 스크롤 동작
 */
const router = VueRouter.createRouter({
  history: VueRouter.createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    } else {
      return { top: 0 }
    }
  },
})

/**
 * Global Navigation Guard
 * @alias module:@router
 * @method beforeEach
 * @param {RouteLocationNormalized} to 대상 Route 객체로 이동
 * @param {RouteLocationNormalized} from 현재 라우트로 오기전 라우트
 * @param {NavigationGuardNext} next 파이프라인의 다음 훅으로 이동, 훅이 없는 경우 네비게이션은 승인
 */
router.beforeEach(async (to, from, next) => {
  store.commit('GlobalStore/footerVisible', to.meta.footerVisible)

  const handlerFinished = await homepageRouteHandler(to, from)

  if (handlerFinished) {
    next(false)
    return
  } else if (
    to.matched.some((routeInfo) => {
      return routeInfo.meta.authRequired
    })
  ) {
    const isSignin = await checkUserInfo()

    if (isSignin || to.query.ismember === 'false') {
      return next()
    }
    await signInWithUrl(routeToUrl(to, true))
  } else {
    next()
  }
})

export default router
